import React, { useCallback, useState } from 'react';
import { useAuth } from '../../Auth/AuthProvider';
import SearchBox from '../../Components/SearchBox';
import Modal from '../../Modal/Modal';
import {
  StorylineDilemma,
  StorylineDilemmaOption,
  StorylineDilemmaOptionType,
} from '../../Types/StorylineDilemma';
import { StorylineDilemmaAttributeOptionCard } from './StorylineDilemmaAttributeOptionCard';
import StorylineDilemmaNewOptionCard from './StorylineDilemmaNewOptionCard';
import StorylineDilemmaNewOptionModal from './StorylineDilemmaNewOptionModal';
import { StorylineDilemmaTextOptionCard } from './StorylineDilemmaTextOptionCard';

interface StorylineDilemmaOptionsSelectViewProps {
  gridCss?: string;
  showDilemmaDetails?: boolean;
  dilemma: StorylineDilemma;
  onOptionSelect: (optionId: string) => void;
  searchString?: string;
  onSearch?: (searchString: string) => void;
  selectedOptionIds?: string[];
  allowCreate?: boolean;
  maxSelectedOptions?: number;
  minSelectedOptions?: number;
  onOptionDeleted?: (optionId: string) => void;
}

const StorylineDilemmaOptionsSelectView: React.FC<
  StorylineDilemmaOptionsSelectViewProps
> = ({
  gridCss = 'grid-cols-4 gap-3 max-md:grid-cols-2 md:max-xl:grid-cols-3 md:max-xl:gap-3 ',
  dilemma,
  onOptionSelect,
  searchString,
  onSearch,
  selectedOptionIds = [],
  allowCreate = true,
  showDilemmaDetails = true,
  maxSelectedOptions = 1,
  minSelectedOptions = 1,
  onOptionDeleted,
}) => {
  const [isCreatingOption, setIsCreatingOption] = useState(false);

  const handleOptionSelect = useCallback(
    (optionId: string) => {
      // If max is 1, we're in single select mode (replace selection)
      if (maxSelectedOptions === 1) {
        // If this option is already selected and we're at min required, do nothing
        if (selectedOptionIds.includes(optionId) && minSelectedOptions > 0) {
          onOptionSelect(null);
          return;
        }
        onOptionSelect(optionId);
        return;
      }

      // For multi-select mode
      if (selectedOptionIds.includes(optionId)) {
        // Allow deselection only if we won't go below min
        if (selectedOptionIds.length > minSelectedOptions) {
          onOptionSelect(optionId);
        }
      } else {
        // Allow selection only if we won't exceed max
        if (selectedOptionIds.length < maxSelectedOptions) {
          onOptionSelect(optionId);
        }
      }
    },
    [onOptionSelect, selectedOptionIds, maxSelectedOptions, minSelectedOptions],
  );

  const handleOptionCreated = (newOption: StorylineDilemmaOption) => {
    dilemma.options.push(newOption);
    setIsCreatingOption(false);
    handleOptionSelect(newOption.id);
  };

  const handleOptionDeleted = useCallback(
    (optionId: string) => {
      // If the deleted option was selected, deselect it
      if (selectedOptionIds.includes(optionId)) {
        handleOptionSelect(optionId);
      }

      // Call the parent's onOptionDeleted handler
      onOptionDeleted?.(optionId);
    },
    [handleOptionSelect, onOptionDeleted],
  );

  const sortedOptions = dilemma.options
    .filter(
      (option) =>
        !searchString?.trim() ||
        option.title?.toLowerCase().includes(searchString.toLowerCase()) ||
        (option.type === StorylineDilemmaOptionType.Attribute &&
          option.attribute?.title
            ?.toLowerCase()
            .includes(searchString.toLowerCase())),
    )
    .sort((a, b) => {
      // First sort by user-created (user-created first)
      if (a.userId && !b.userId) return -1;
      if (!a.userId && b.userId) return 1;
      // Then by order
      return a.order - b.order;
    });

  return (
    <>
      <div className="flex flex-col gap-3 max-h-full">
        <div className="flex justify-between items-center py-3 sticky top-0 z-10 bg-white">
          <div className="flex-col justify-start items-start gap-2 inline-flex">
            <div className="text-xl font-semibold text-left max-lg:text-base max-lg:font-bold">
              {dilemma.question}
            </div>
            {showDilemmaDetails && (
              <div className="text-sm leading-5 max-w-[834px] text-neutral-800 text-opacity-70 text-left max-lg:hidden">
                {dilemma.details}
              </div>
            )}
          </div>
          {onSearch && (
            <SearchBox
              searchString={searchString}
              onSearch={onSearch}
              placeholder="Search..."
            />
          )}
        </div>
        <div
          className={`
            grid overflow-auto p-1 ${gridCss}
            md:max-xl:mt-1
            flex-1
          `}
        >
          {allowCreate && (
            <StorylineDilemmaNewOptionCard
              onClick={() => setIsCreatingOption(true)}
            />
          )}
          {sortedOptions.map((option) => (
            <DilemmaOption
              key={option.id}
              option={option}
              isSelected={selectedOptionIds.includes(option.id)}
              onSelect={handleOptionSelect}
              onOptionDeleted={handleOptionDeleted}
            />
          ))}
        </div>

        <Modal
          isOpen={isCreatingOption}
          onClose={() => setIsCreatingOption(false)}
          maxWidthClass="max-w-[500px]"
        >
          <StorylineDilemmaNewOptionModal
            dilemmaId={dilemma.id}
            onOptionCreated={handleOptionCreated}
            onCancel={() => setIsCreatingOption(false)}
          />
        </Modal>
      </div>
    </>
  );
};

export default StorylineDilemmaOptionsSelectView;

const DilemmaOption: React.FC<{
  option: StorylineDilemmaOption;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onOptionDeleted?: (optionId: string) => void;
}> = ({
  option,
  isSelected,
  onSelect,
  onOptionDeleted,
}: {
  option: StorylineDilemmaOption;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onOptionDeleted?: (optionId: string) => void;
}) => {
  const { userId } = useAuth();

  switch (option.type) {
    case StorylineDilemmaOptionType.Attribute:
      return (
        <div className="relative">
          <StorylineDilemmaAttributeOptionCard
            key={option.id}
            option={option}
            isSelected={isSelected}
            onClick={() => onSelect(option.id)}
          />
        </div>
      );
    case StorylineDilemmaOptionType.Text:
      return (
        <div className="relative">
          {option.userId === userId ? (
            <div className="justify-center self-end px-2.5 py-1.5 text-sm tracking-wide text-center text-white bg-blue-600 rounded-br-xl rounded-tl-xl shadow-sm absolute top-0 left-0 z-10">
              Created By Me
            </div>
          ) : (
            option.userId && (
              <div className="justify-center self-end px-2.5 py-1.5 text-sm tracking-wide text-center text-white bg-purple-600 rounded-br-xl rounded-tl-xl shadow-sm absolute top-0 left-0 z-10">
                Created By Co-Author
              </div>
            )
          )}
          <StorylineDilemmaTextOptionCard
            key={option.id}
            option={option}
            isSelected={isSelected}
            onClick={() => onSelect(option.id)}
            onOptionDeleted={onOptionDeleted}
          />
        </div>
      );
    default:
      throw new Error(`Unknown option type: ${option.type}`);
  }
};
